"use client";

import { useEffect, useState } from "react";

/** De `.env`; el respaldo es el dominio real, para que un `.env` incompleto no deje el sitio sin chat. */
const SOPORTE_URL = process.env.NEXT_PUBLIC_SOPORTE_URL ?? "https://soporte.vecipp.com";

/**
 * **El chat de soporte para visitantes, embebido desde `soporte.vecipp.com/widget`** (2026-09-26).
 *
 * Hasta ese día este sitio tenía su propia copia del widget, de sus 3 endpoints y de `notification-sound.ts`, idéntica a
 * la del otro sitio. Ahora el widget vive una sola vez en `Vecipp-Soporte` y acá solo queda el iframe.
 *
 * 🔑 **El iframe cambia de tamaño con el chat**: cerrado, solo el botón (96 × 96, con el botón a 24 px del borde, donde
 * estaba antes); abierto, lo que ocupa el panel. El widget avisa con `postMessage` y acá se comprueba que el mensaje
 * venga de Soporte antes de hacerle caso.
 */
type Estado = { abierto: boolean; grande: boolean; saludo: boolean };

export function WidgetSoporte({ origen }: { readonly origen: "ayuda" | "landing" }) {
  const [estado, setEstado] = useState<Estado>({ abierto: false, grande: false, saludo: false });

  useEffect(() => {
    const origenSoporte = new URL(SOPORTE_URL).origin;
    function alMensaje(evento: MessageEvent) {
      if (evento.origin !== origenSoporte) return;
      const datos = evento.data as ({ tipo?: string } & Partial<Estado>) | null;
      if (datos?.tipo === "vecipp-widget") {
        setEstado({ abierto: Boolean(datos.abierto), grande: Boolean(datos.grande), saludo: Boolean(datos.saludo) });
      }
    }
    window.addEventListener("message", alMensaje);
    return () => window.removeEventListener("message", alMensaje);
  }, []);

  return (
    <iframe
      title="Chat de soporte"
      src={`${SOPORTE_URL}/widget?origen=${origen}`}
      className="fixed right-0 bottom-0 z-[70] border-0 bg-transparent"
      // 2026-10-04: el chat de Ayuda es el chat flotante de Soporte. Cerrado, el botón (y el globo «¿Necesitas ayuda?»
      // si está a la vista); abierto, el panel de 400 × 640 más el botón; agrandado, todo el alto.
      style={
        estado.abierto
          ? estado.grande
            ? { width: "min(820px, 100vw)", height: "100dvh", colorScheme: "normal" }
            : { width: "min(450px, 100vw)", height: "min(760px, 100dvh)", colorScheme: "normal" }
          : estado.saludo
            ? { width: 340, height: 104, colorScheme: "normal" }
            : { width: 104, height: 104, colorScheme: "normal" }
      }
    />
  );
}
